"use client";

import React, { useEffect } from "react";

import { getFreshchatCustomData, tracker } from "@/customAnalytics/tracking";
import { CHATBOT_ALLOW_ATTR } from "./chatbotGuardSnippet";

declare global {
  interface Window {
    fcWidget?: {
      init: (config: {
        token: string;
        host: string;
        widgetUuid: string;
      }) => void;
      on?: (event: string, handler: (...args: unknown[]) => void) => void;
      user?: { setProperties: (data: Record<string, string>) => void };
    };
  }
}

/** Matches IIT KGP Freshchat embed id (`Freshchat-js-sdk`). */
const FRESHCHAT_SCRIPT_ID = "Freshchat-js-sdk";

const EMAIL_REGEX = /^[^\s]+@[^\s]+\.[^\s]+$/;

function isEmailLike(content: string): boolean {
  const trimmed = (content || "").trim();
  return EMAIL_REGEX.test(trimmed) || (trimmed.includes("@") && trimmed.length > 5);
}

/**
 * Freshdesk Freshchat widget — ported from iitkgp-campus-portal/webapp/src/app/script.tsx.
 * Configure via NEXT_PUBLIC_FRESHDESK_CHAT_TOKEN, NEXT_PUBLIC_FRESHDESK_CHAT_HOST,
 * NEXT_PUBLIC_FRESHDESK_CHAT_WIDGET_UUID (same values as fcWidget.init in source repo).
 */
type FreshdeskProps = {
  token?: string | null;
  host?: string | null;
  widgetUuid?: string | null;
};

const Freshdesk = ({ token: tokenProp, host: hostProp, widgetUuid: widgetUuidProp }: FreshdeskProps = {}) => {
  const token = (tokenProp && tokenProp.trim()) || process.env.NEXT_PUBLIC_FRESHDESK_CHAT_TOKEN;
  const host = (hostProp && hostProp.trim()) || process.env.NEXT_PUBLIC_FRESHDESK_CHAT_HOST;
  const widgetUuid =
    (widgetUuidProp && widgetUuidProp.trim()) ||
    process.env.NEXT_PUBLIC_FRESHDESK_CHAT_WIDGET_UUID;

  useEffect(() => {
    // Only rendered when the CMS selected Freshdesk, so missing config is a broken deploy.
    if (!token || !host || !widgetUuid) {
      console.warn(
        "[Freshdesk] chatbot_provider is 'freshdesk' but NEXT_PUBLIC_FRESHDESK_CHAT_TOKEN / _HOST / _WIDGET_UUID are missing from this build — no widget will load."
      );
      return;
    }

    let cancelled = false;
    let eventsAttached = false;
    let appendedScript: HTMLScriptElement | null = null;
    const normalizedHost = host.replace(/\/$/, "");

    // Attach Freshchat conversion tracking (ported from iitkgp FreshchatProvider).
    // Fires lead events to RudderStack + gtag/dataLayer; on email submission it
    // fires `lead_submitted`, which records the Google Ads conversion.
    const attachFreshchatTracking = () => {
      const fc = window.fcWidget;
      if (!fc?.on || eventsAttached) return;
      eventsAttached = true;

      try {
        fc.user?.setProperties(getFreshchatCustomData());
      } catch (e) {
        console.warn("Freshchat setProperties failed", e);
      }

      fc.on("widget:opened", () => {
        tracker({
          event: "modal_view",
          data: {
            section: "sticky_support",
            component_name: "freshchat",
            label: "freshchat_widget",
            medium: "freshchat",
          },
        });
      });

      fc.on("widget:closed", () => {
        tracker({
          event: "modal_closed_clicked",
          data: {
            section: "sticky_support",
            component_name: "freshchat",
            label: "freshchat_widget",
            medium: "freshchat",
          },
        });
      });

      fc.on("message:sent", (data: unknown) => {
        const payload = data as {
          message?: { messageFragments?: { content?: string }[] };
        };
        const content = payload?.message?.messageFragments?.[0]?.content ?? "";

        tracker({
          event: "lead_cta_click",
          data: {
            section: "sticky_support",
            component_name: "chat_with_us",
            label: content || "freshchat_message_sent",
            medium: "freshchat",
          },
        });

        if (isEmailLike(content)) {
          tracker({
            event: "lead_submitted",
            data: {
              section: "sticky_support",
              component_name: "chat_with_us",
              label: "email_submitted",
              medium: "freshchat",
              entity_name: "freshchat_widget",
              email_id: content.trim(),
            },
          });
        }
      });
    };

    const initFreshChat = () => {
      if (cancelled || typeof window === "undefined" || !window.fcWidget) return;
      window.fcWidget.init({
        token,
        host: normalizedHost,
        widgetUuid,
      });
      attachFreshchatTracking();
    };

    const initialize = () => {
      if (cancelled) return;
      const doc = document;
      if (doc.getElementById(FRESHCHAT_SCRIPT_ID)) {
        initFreshChat();
        return;
      }
      const script = doc.createElement("script");
      script.id = FRESHCHAT_SCRIPT_ID;
      script.async = true;
      script.src = `${normalizedHost}/js/widget.js`;
      // This injection is provider-gated, so opt it out of the ChatbotGuard block.
      script.setAttribute(CHATBOT_ALLOW_ATTR, "");
      script.onload = initFreshChat;
      doc.head.appendChild(script);
      appendedScript = script;
    };

    // Defer the chat bundle (~700 KiB JS + a 367 KiB font) off the critical
    // path. The widget is invisible at first paint, so loading it eagerly made it
    // compete with the hero/LCP at HIGH priority on slow connections. Load it on
    // the FIRST real user interaction (scroll/move/tap/key) — by which point the
    // page has long since painted — with a late timer backstop so users who never
    // interact still eventually get chat without ever touching the LCP window.
    // NOTE: deliberately NOT requestIdleCallback — it fires the instant the main
    // thread quiets (~immediately), which defeats the deferral.
    let triggered = false;
    let backstop: number | undefined;
    const INTERACTION_EVENTS: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
    ];

    const clearTriggers = () => {
      INTERACTION_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onTrigger),
      );
      if (backstop !== undefined) {
        window.clearTimeout(backstop);
        backstop = undefined;
      }
    };

    function onTrigger() {
      if (triggered || cancelled) return;
      triggered = true;
      clearTriggers();
      initialize();
    }

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onTrigger, { once: true, passive: true }),
    );
    backstop = window.setTimeout(onTrigger, 6000);

    return () => {
      cancelled = true;
      clearTriggers();
      if (appendedScript?.parentNode) {
        appendedScript.parentNode.removeChild(appendedScript);
      }
    };
  }, [token, host, widgetUuid]);

  return null;
};

export default Freshdesk;
